import { useState } from "react";
import { Eye, Monitor, Smartphone, Mail, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const sampleEmailHTML = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: #f5f5f5; }
    .container { max-width: 600px; margin: 0 auto; background: white; border-radius: 8px; overflow: hidden; }
    .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center; }
    .header h1 { color: white; margin: 0; font-size: 28px; }
    .header p { color: rgba(255,255,255,0.9); margin: 10px 0 0; }
    .content { padding: 30px; }
    .content h2 { color: #333; margin-top: 0; }
    .content p { color: #666; line-height: 1.6; }
    .button { display: inline-block; background: #667eea; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: bold; }
    .footer { background: #f9f9f9; padding: 20px 30px; text-align: center; color: #999; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Welcome to Our Newsletter</h1>
      <p>Stay updated with our latest news</p>
    </div>
    <div class="content">
      <h2>Hello there!</h2>
      <p>Thank you for subscribing to our newsletter. We're excited to have you on board and can't wait to share our latest updates with you.</p>
      <p>Click the button below to explore our latest offerings:</p>
      <p style="text-align: center; margin: 30px 0;">
        <a href="#" class="button">Explore Now</a>
      </p>
      <p>If you have any questions, feel free to reach out to our support team.</p>
    </div>
    <div class="footer">
      <p>© 2024 Your Company. All rights reserved.</p>
      <p>123 Main Street, City, Country</p>
    </div>
  </div>
</body>
</html>`;

interface EmailClient {
  id: string;
  name: string;
  icon: string;
  description: string;
  cssOverrides: string;
  notes: string[];
}

const emailClients: EmailClient[] = [
  {
    id: "gmail",
    name: "Gmail",
    icon: "📧",
    description: "Web & Mobile",
    cssOverrides: `
      /* Gmail strips many CSS properties */
      .button { background-color: #667eea !important; }
      /* Gmail may not support certain fonts */
      body { font-family: Arial, Helvetica, sans-serif !important; }
    `,
    notes: [
      "Strips <style> tags in head - inline styles preferred",
      "Removes background-image on body",
      "May clip emails over 102KB",
      "Supports basic CSS but limited animations"
    ]
  },
  {
    id: "outlook",
    name: "Outlook",
    icon: "📬",
    description: "Desktop & Web",
    cssOverrides: `
      /* Outlook uses Word rendering engine */
      .button { 
        background-color: #667eea !important;
        border-radius: 0 !important; /* No border-radius in Outlook */
      }
      .container { border-radius: 0 !important; }
      .header { background: #667eea !important; } /* No gradients */
      body { font-family: 'Segoe UI', Arial, sans-serif !important; }
    `,
    notes: [
      "Uses Word rendering engine (limited CSS)",
      "No support for border-radius",
      "No CSS gradients - use background images",
      "Tables recommended for layout",
      "Max-width may not work - use fixed width"
    ]
  },
  {
    id: "apple",
    name: "Apple Mail",
    icon: "🍎",
    description: "macOS & iOS",
    cssOverrides: `
      /* Apple Mail has good CSS support */
      body { -webkit-font-smoothing: antialiased; }
    `,
    notes: [
      "Best CSS support among email clients",
      "Supports CSS3 and animations",
      "Supports web fonts",
      "Dark mode support available"
    ]
  },
  {
    id: "yahoo",
    name: "Yahoo Mail",
    icon: "📨",
    description: "Web",
    cssOverrides: `
      /* Yahoo has decent support but some quirks */
      body { font-family: 'Helvetica Neue', Arial, sans-serif !important; }
      .button { border-radius: 4px !important; }
    `,
    notes: [
      "Adds extra spacing to elements",
      "May scale images unexpectedly",
      "Inline styles recommended",
      "Limited media query support"
    ]
  }
];

export const EmailPreviewTool = () => {
  const [htmlInput, setHtmlInput] = useState("");
  const [activeClient, setActiveClient] = useState("gmail");
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");

  const loadSample = () => {
    setHtmlInput(sampleEmailHTML);
  };

  const clearInput = () => {
    setHtmlInput("");
  };

  const activeClientData = emailClients.find(c => c.id === activeClient)!;

  const renderPreview = () => {
    if (!htmlInput.trim()) return null;

    // Apply client-specific CSS overrides
    const modifiedHTML = htmlInput.replace(
      '</style>',
      `${activeClientData.cssOverrides}</style>`
    );

    return modifiedHTML;
  };

  const previewContent = renderPreview();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Eye className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Email Client Preview
          </h3>
          <p className="text-sm text-muted-foreground">
            See how your email renders across different email clients
          </p>
        </div>
      </div>

      {/* Input Section - Full Width */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">
            Email HTML
          </label>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSample}
              className="text-xs text-primary hover:text-primary/80"
            >
              Load Sample
            </Button>
            {htmlInput && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearInput}
                className="text-xs text-muted-foreground"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
        <Textarea
          value={htmlInput}
          onChange={(e) => setHtmlInput(e.target.value)}
          placeholder="Paste your email HTML here..."
          className="min-h-[160px] font-mono text-xs resize-none"
        />
      </div>

      {/* Preview Section - Full Width Below */}
      <div className="space-y-3">
        {/* Client Tabs & View Mode */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Tabs value={activeClient} onValueChange={setActiveClient}>
            <TabsList className="h-auto p-1">
              {emailClients.map((client) => (
                <TabsTrigger
                  key={client.id}
                  value={client.id}
                  className="text-xs px-2 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <span className="mr-1">{client.icon}</span>
                  {client.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          
          {/* View Mode Toggle */}
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode("desktop")}
              className={`p-1.5 rounded transition-colors ${
                viewMode === "desktop" 
                  ? "bg-background text-foreground shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("mobile")}
              className={`p-1.5 rounded transition-colors ${
                viewMode === "mobile" 
                  ? "bg-background text-foreground shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Preview Frame - Full Width for Desktop */}
        <div 
          className={`bg-muted/50 rounded-xl border border-border overflow-hidden transition-all ${
            viewMode === "mobile" ? "max-w-[375px] mx-auto" : "w-full"
          }`}
        >
          {/* Client Header */}
          <div className="bg-muted px-3 py-2 border-b border-border flex items-center gap-2">
            <span className="text-lg">{activeClientData.icon}</span>
            <div>
              <p className="text-xs font-medium text-foreground">{activeClientData.name}</p>
              <p className="text-[10px] text-muted-foreground">{activeClientData.description}</p>
            </div>
          </div>

          {/* Preview Content - Taller for Desktop */}
          <div className={`bg-white overflow-auto ${viewMode === "mobile" ? "h-[500px]" : "h-[450px]"}`}>
            {previewContent ? (
              <iframe
                srcDoc={previewContent}
                className="w-full h-full border-0"
                title="Email Preview"
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Mail className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">Paste HTML to see preview</p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={loadSample}
                  className="mt-1 text-xs"
                >
                  Or load a sample email
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Client Notes */}
        {htmlInput && (
          <div className="bg-muted/50 rounded-xl p-3">
            <h4 className="text-xs font-medium text-foreground mb-2 flex items-center gap-1.5">
              <span>{activeClientData.icon}</span>
              {activeClientData.name} Rendering Notes
            </h4>
            <ul className="space-y-1">
              {activeClientData.notes.map((note, i) => (
                <li key={i} className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                  <span className="text-primary mt-0.5">•</span>
                  {note}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Quick Tips */}
      <div className="pt-4 border-t border-border">
        <h4 className="text-sm font-medium text-foreground mb-2">Cross-Client Best Practices</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { tip: "Use inline styles", desc: "Many clients strip <style> tags" },
            { tip: "Tables for layout", desc: "Most reliable across all clients" },
            { tip: "600px max width", desc: "Optimal for most email clients" },
            { tip: "Test dark mode", desc: "Check color contrast in dark themes" },
          ].map((item, i) => (
            <div key={i} className="bg-muted/50 rounded-lg p-2.5">
              <p className="text-xs font-medium text-foreground">{item.tip}</p>
              <p className="text-[10px] text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};